<template>
  <page-header-wrapper>
    <div class="business-page erp-list-page baidao-list-page accounts-receivable-summary-page">
      <section class="erp-search-card">
        <a-form layout="inline">
          <a-row :gutter="24">
            <a-col :md="8" :sm="24">
              <a-form-item label="起止日期" required>
                <a-range-picker v-model="dateRange" style="width: 100%" />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="客商">
                <a-select
                  v-model="query.customerId"
                  allow-clear
                  show-search
                  option-filter-prop="children"
                  placeholder="全部客商">
                  <a-select-option v-for="item in customerOptions" :key="item.id" :value="item.id">
                    {{ item.customerCode }} · {{ item.customerName }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="关键字">
                <a-input v-model="query.keyword" allow-clear placeholder="编码或名称" @pressEnter="search" />
              </a-form-item>
            </a-col>
            <a-col :md="24" :sm="24" class="erp-search-actions">
              <a-button type="primary" icon="search" @click="search">查询</a-button>
              <a-button icon="redo" @click="resetQuery">重置</a-button>
            </a-col>
          </a-row>
        </a-form>
      </section>

      <section class="erp-table-card">
        <div class="erp-table-toolbar">
          <div>
            <h3>应收账款汇总表</h3>
            <p>按客商汇总：本期应收 = 期间内已审核销售对账应收 + 客商费用单；本期已收 = 核销对账的收款（含负数退款）；期末 = 期初 + 本期应收 − 本期已收。</p>
          </div>
          <div class="erp-toolbar-actions">
            <span v-if="pagination.total" class="accounts-receivable-summary-totals">
              合计 期初 <strong class="erp-money">¥{{ formatMoney(sumOpeningBalance) }}</strong>
              应收 <strong class="erp-money">¥{{ formatMoney(sumPeriodReceivable) }}</strong>
              已收 <strong class="erp-money">¥{{ formatMoney(sumPeriodReceived) }}</strong>
              期末 <strong class="erp-money">¥{{ formatMoney(sumClosingBalance) }}</strong>
            </span>
            <a-button
              v-hasPermi="['erp:report:accounts-receivable-summary:export']"
              icon="download"
              :loading="exporting"
              :disabled="!pagination.total"
              @click="doExport">导出</a-button>
          </div>
        </div>
        <a-table
          row-key="id"
          bordered
          size="middle"
          :columns="tableColumns"
          :data-source="rows"
          :loading="loading"
          :pagination="pagination"
          :scroll="tableScroll"
          :locale="{ emptyText: '暂无数据' }"
          @change="onTableChange">
          <template slot="text" slot-scope="value">
            <span :title="displayValue(value)">{{ displayValue(value) }}</span>
          </template>
          <template slot="money" slot-scope="value"><span class="erp-money">¥{{ formatMoney(value) }}</span></template>
        </a-table>
      </section>
    </div>
  </page-header-wrapper>
</template>

<script>
import moment from 'moment'
import {
  pageAccountsReceivableSummaryReport,
  listAccountsReceivableSummaryCustomerOptions,
  exportAccountsReceivableSummaryReport
} from '@/api/erp/accountsReceivableSummaryReport'

const emptyQuery = () => ({
  customerId: undefined,
  keyword: ''
})

export default {
  name: 'ErpAccountsReceivableSummaryReport',
  data () {
    return {
      dateRange: [moment().startOf('month'), moment()],
      query: emptyQuery(),
      customerOptions: [],
      rows: [],
      loading: false,
      exporting: false,
      pagination: { current: 1, pageSize: 20, total: 0, showSizeChanger: true, showTotal: t => `共 ${t} 条` },
      sumOpeningBalance: 0,
      sumPeriodReceivable: 0,
      sumPeriodReceived: 0,
      sumClosingBalance: 0,
      tableScroll: { x: 960 },
      sortField: undefined,
      sortOrder: undefined,
      tableColumns: [
        { title: '客商编码', dataIndex: 'customerCode', width: 140, scopedSlots: { customRender: 'text' }, sorter: true },
        { title: '客商名称', dataIndex: 'customerName', width: 220, scopedSlots: { customRender: 'text' }, sorter: true },
        { title: '期初余额', dataIndex: 'openingBalance', width: 130, align: 'right', scopedSlots: { customRender: 'money' }, sorter: true },
        { title: '本期应收', dataIndex: 'periodReceivable', width: 130, align: 'right', scopedSlots: { customRender: 'money' }, sorter: true },
        { title: '本期已收', dataIndex: 'periodReceived', width: 130, align: 'right', scopedSlots: { customRender: 'money' }, sorter: true },
        { title: '期末余额', dataIndex: 'closingBalance', width: 130, align: 'right', scopedSlots: { customRender: 'money' }, sorter: true }
      ]
    }
  },
  created () {
    this.loadCustomerOptions()
    this.loadData()
  },
  methods: {
    displayValue (v) {
      return v == null || v === '' ? '—' : v
    },
    formatMoney (v) {
      const n = Number(v)
      if (Number.isNaN(n)) return '0.00'
      return n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    queryParams () {
      const range = this.dateRange || []
      return {
        ...this.query,
        dateStart: range[0] ? range[0].format('YYYY-MM-DD') : undefined,
        dateEnd: range[1] ? range[1].format('YYYY-MM-DD') : undefined,
        pageNum: this.pagination.current,
        pageSize: this.pagination.pageSize
      }
    },
    loadCustomerOptions () {
      listAccountsReceivableSummaryCustomerOptions().then(res => {
        this.customerOptions = res.data || []
      })
    },
    applyClientSort () {
      if (!this.sortField || !this.sortOrder) return
      const field = this.sortField
      const dir = this.sortOrder === 'ascend' ? 1 : -1
      const isMoney = field !== 'customerCode' && field !== 'customerName'
      this.rows = this.rows.slice().sort((a, b) => {
        const av = a[field]
        const bv = b[field]
        if (isMoney) return (Number(av || 0) - Number(bv || 0)) * dir
        return String(av || '').localeCompare(String(bv || ''), 'zh-CN') * dir
      })
    },
    loadData () {
      if (!this.dateRange || this.dateRange.length !== 2) {
        this.$message.warning('请选择起止日期')
        return
      }
      this.loading = true
      pageAccountsReceivableSummaryReport(this.queryParams()).then(res => {
        const data = res.data || {}
        this.rows = data.records || []
        this.applyClientSort()
        this.pagination.total = Number(data.total || 0)
        this.pagination.current = Number(data.current || 1)
        this.pagination.pageSize = Number(data.size || 20)
        this.sumOpeningBalance = data.sumOpeningBalance
        this.sumPeriodReceivable = data.sumPeriodReceivable
        this.sumPeriodReceived = data.sumPeriodReceived
        this.sumClosingBalance = data.sumClosingBalance
      }).finally(() => {
        this.loading = false
      })
    },
    search () {
      this.pagination.current = 1
      this.loadData()
    },
    resetQuery () {
      this.dateRange = [moment().startOf('month'), moment()]
      this.query = emptyQuery()
      this.sortField = undefined
      this.sortOrder = undefined
      this.search()
    },
    onTableChange (pag, filters, sorter) {
      const pageChanged = pag.current !== this.pagination.current || pag.pageSize !== this.pagination.pageSize
      this.pagination.current = pag.current
      this.pagination.pageSize = pag.pageSize
      if (sorter && sorter.field) {
        this.sortField = sorter.field
        this.sortOrder = sorter.order
      } else {
        this.sortField = undefined
        this.sortOrder = undefined
      }
      if (pageChanged) {
        this.loadData()
      } else {
        this.applyClientSort()
      }
    },
    async doExport () {
      this.exporting = true
      try {
        const params = { ...this.queryParams() }
        delete params.pageNum
        delete params.pageSize
        const res = await exportAccountsReceivableSummaryReport(params)
        const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `应收账款汇总_${moment().format('YYYYMMDDHHmmss')}.xlsx`
        link.click()
        URL.revokeObjectURL(link.href)
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<style scoped lang="less">
.accounts-receivable-summary-totals {
  margin-right: 12px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
  strong {
    margin: 0 8px 0 2px;
  }
}
</style>
